export const automationTool = {
    "intent-collection": {
      title: "What is Intent Collection? | anydone",
      content: `

    <p>Intent Collection is an automation model that helps AI to respond. An intent represents the goal or purpose behind a user’s message when interacting with the AI. For example, when a user asks, “What is my order status?”, the intent could be identified as "Check Order Status.</p>

    <p>It uses Natural Language Understanding (NLU) to analyze the user’s message and determine the intent. This process involves two key steps:</p>

    <p> <span class="underline-text">Classifying Intents: </span> The AI identifies the user’s purpose by analyzing their input. With the help of Natural Language Processing (NLP), Machine Learning, and pattern recognition to recognize and categorize intents accurately.
    </p>

    <p> <span class="underline-text">Automating Responses: </span> Once the intent is classified, the AI selects the appropriate response or even performs predefined action, ensuring the interaction is efficient and relevant to the user's needs or query.
    </p>
    <p>With anydone’s  Intent Collection model, users can define a comprehensive set of intents that reflect and allow AI to handle a wide range of user queries and automate responses effectively. This makes the interaction smooth, personalized, and highly efficient.</p>

    <h3 class="headingThree">How to create an Intent Collection?</h3>
    <ul class="steps-list">
        <li>Log in to your anydone account.</li>
        <li>Click on the <strong>Automation Tools</strong> icon from the left side of your dashboard.</li>
        <li>Under the <strong>AI</strong>, select <strong>Intent Collection.</strong></li>
        <li>Click the <strong>+</strong> button from the bottom right corner.</li>
        <li>Enter a <strong>name</strong> and <strong>description</strong>.</li>
        <li>Click the <strong>Create</strong> button.</li>
    </ul>

    <p>Continue the process by clicking the + button from the bottom-right corner.</p>
    <ul class="steps-list">
        <li>Here, enter the <strong>Intent Name.</strong></li>
        <li>Define the <strong>Threshold</strong>
        (Threshold is a predefined confidence score / probability value used to determine whether a detected intent is considered a match or not.)</li>
        <li>Add all phrases that correspond to your intent.</li>
        <li>Finally, click the <strong>Create</strong> button and save the intent.</li>
    </ul>

    <h3 class="headingThree">Using the Intent Collection Model</h3>
    <p>After creating and saving the intent collection, you have two ways to use it as an AI Agent:</p>
    <ul class="no-listing">
        <li>- Create and use the Intent Collection model directly in the AI Studio.</li>
        <li>- Add the Intent Collection to the Knowledge Engine and build an AI Agent tailored to your requirements.</li>
    </ul>
    <p>You can choose either way based on your preference and create an Intent Collection that aligns with your specific goals and workflows.</p>

    <h3 class="headingThree">How to add an Intent Collection to the Knowledge Engine</h3>
    <ul class="steps-list">
        <li>Log in to your anydone account.</li>
        <li>Click on the <strong>Automation Tools</strong> icon from the left side of your dashboard.</li>
        <li>Under <strong>AI</strong>, select <strong>Knowledge Engine.</strong></li>
        <li>Click the <strong>+</strong> button from the bottom-right corner.</li>
        <li>Enter a <strong>name</strong> and <strong>description</strong>.</li> 
        <li>Click on the <strong>Source</strong>.</li>
        <li>Click on the drop-down menu of <strong>Intent Collection</strong>.</li>
        <li>Select the intent collection you’ve created.</li>
        <li>Add the <strong>Reply Text</strong> (click on the Add Variation button to add reply text as many as you want).</li>
        <li>Define the Handoff Rule (here we’ve selected <strong>Fixed Replies</strong>).</li>
        <li>Finally, click the <strong>Create</strong> button.</li>
    </ul>

    <h3 class="headingThree">Tips</h3>
        <ul class="no-listing">
            <li>- The Handoff Rule is used when any “Query” goes beyond the predefined text or scope. From here, you can define what actions to take when such events occur, you can choose between:</li>
            <li>- Knowledge Graph, Knowledge Base, Knowledge Engine, Do Nothing, Fixed Reply, or</li>
            <li>- Generative Model: When selected, you can choose between the default Anydone model or a model created with a third party.</li>
        </ul>
    `
    },
    "entity":{
        title:"What is Entity? | anydone",
        content:`
        <p>Entity adds meaning and context to a user's input, enhancing the AI Agent's ability to generate accurate responses. While intents determine what the user wants, entities provide additional details or values that refine the intent. </p>

        <p>These can include information such as time, location, person, product name, emails, phone number or any specific data that gives the AI more clarity about the user's request.</p>

        <p>For example:</p>
        <p>In the query "What’s the weather in New York tomorrow?", the intent is "Get Weather Update," and the entity is "New York" (location) and "tomorrow" (time). Using Natural Language Processing (NLP), AI can identify and extract entities from user inputs. This process involves:
        </p>

         <ul class="no-listing">
            <li>- <strong>Detecting Entities:</strong> The AI goes through the user’s conversations for keywords or data patterns that match predefined entity categories, such as emails, names, or numbers.</li>
            <li>- <strong>Associating Entities with Intents:</strong> Once extracted, the entities are tied to the corresponding intent to provide a more specific understanding of the user's request.</li>
        </ul>

        <p>By defining and managing entities, anydone users can build AI Agents to handle complex queries and delivers precise and actionable responses.</p>

        <h3 class="headingThree">How to create an Entity?</h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click the Automation Tools icon from the left side of your dashboard.</li>
            <li>Under the AI, select Entity.</li>
            <li>Click the + button, from the bottom-right corner.</li>
            <li>Enter a name and description for the Entity.</li>
            <li>Now, choose the Entity type: Regex or Synonym.</li>
        </ul>

        <h3>Tips</h3>
        <ul class="no-listing">
            <li><span class="underline-text">Regex:</span> <br>
            Regex (short for Regular Expression) is a pattern-matching technique used to identify or extract entities based on specific text patterns.
            </li>
            <li>
            If you select a Regular Expression (Regex), start by defining the Regex expression in the placeholder. Likewise, if you're creating an entity to extract email addresses, the Regex expression could be [a-z]*@[a-z]*.[a-z]*
            </li>
            <li>
            Next, add a relevant "Prompt." For example, if you want to extract email addresses, a possible prompt could be: "What is your email address?” Please provide your email." entity-email", and so on. 
            </li>
            <li><span class="underline-text">Synonym</span>
            Synonyms refer to alternate names, terms, or phrases that represent the same entity.
            </li>
            <li>If you select Synonym, then you need to define its Reference Value, Synonyms, and Prompt. For example, if you add "New York City" in Reference Value, then Synonyms could be "NYC," "New York," "Big Apple," whereas Prompt could be "Enter the location of the event" or "Give the address where the event will occur" or anything that is contextual to your entity. 
            </li>
        </ul>   
        `
    },
    "knowledge-base":{
        title:"What is a Knowledge Base? | anydone",
        content:`
        <p>A Knowledge Base serves as a central repository of information that empowers AI to provide accurate and detailed responses to user queries. It acts as the crucial element of the AI’s ability to understand and deliver contextually relevant answers by storing, organizing, and managing critical knowledge in a structured format.</p>

        <p>Anydone Knowledge Base helps users build a comprehensive resource that supports  human-like yet contextual and user specific automated responses. In anydone you can build knowledge base in three ways: </p>

        <ul class="no-listing">
            <li><span>-</span> <strong>Add URLs:</strong> Link to external web pages or online resources that the AI Agent can refer to for providing real-time, contextual and issue specific responses. </li>
            <li><span>-</span> <strong>Upload Documents:</strong> Add internal files, such as manuals, guidelines, FAQs, or reference documents, and enrich the AI’s knowledge so that it can give responses more contextually. </li>
            <li><span>-</span> <strong> Write Text Manually:</strong> Write a text manually and create a that delivers specific response and specific queries. </li>
        </ul>

        <p>Knowledge base enables the AI to fetch precise answers to user questions. Moreover anydone allows its users to easily update their knowledge base to keep the AI Agents information current and relevant. </p>

        <h3 class="headingThree">How to create a Knowledge Base?</h3>
        <ul class="steps-list">
            <li>Log in to your <span class="text-blue">anydone account .</span></li>
            <li>Click on the <strong>Automation Tools</strong> icon from the left side of your dashboard</li>
            <li>Under <strong>AI</strong>, select <strong>Knowledge Base</strong> from the options.</li>
            <li>Enter a <strong>name</strong> and <strong>description</strong>.</li>
            <li>Select the knowledge base <strong>Source</strong> (here we have selected URL as a source)</li>
            <li>Add the <strong>URL.</strong></li>
            <li>Define the <strong>Update Frequency.</strong></li>
            <li>Finally, click the <strong>Create</strong> button.</li>
        </ul>

        <p>In anydone you create a knowledge base using multiple sources — such as Text, URLs, Document. You can add as many sources as needed for one knowledge base, enabling the AI Agent to reference diverse materials when responding to queries.</p>

        <p>You can reorder your sources, defining the search sequence that the AI Agent follows when retrieving answers.</p>

        <h3 class="headingThree">How Source Ordering Works</h3>
        <p>When a user submits a query, the AI Agent looks into your Knowledge Base in the exact order the sources are listed: </p>

        <ul>
            <li>It searches the first source for relevant answers.</li>
            <li>If a sufficient match isn’t found, it proceeds to the second source.</li>
        </ul>

        <p>The process continues through the remaining sources until the AI finds the most suitable response. This allows you to control the flow of knowledge, giving priority to more accurate, recent, or trusted content.</p>

        <h3 class="headingThree">Managing the Order</h3>
        <p>You can change the order of your Knowledge sources in two ways:</p>
        <ul>
            <li><span class="underline-text">Drag and Drop:</span> Simply click and drag a source to reposition it within the list.</li>
            <li><span class="underline-text">Dropdown Selector:</span> Use the dropdown next to each source to assign a specific position number.</li>
        </ul>

        <h3 class="headingThree">Tips:</h3>
        <ul class="no-listing">
            <li>- There are three ways to add a knowledge base source: URL, Text, and Document. 
            <ul class="no-listing">
                <li>URL: Enter a link to the online resource for your knowledge base.</li>
                <li>Text: Add the text content for your knowledge base.</li>
                <li>Document: Upload a document that contains the relevant content for your knowledge base.</li>
            </ul>
            </li>

            <li>- Update Frequency is where you define how often the content of your knowledge base source should be updated automatically.</li>
        </ul>

        <h3 class="headingThree">Creating Knowledge Engine with Knowledge Base</h3>
        <p>After creating the knowledge base, the next step is to add it to the Knowledge Engine. Without this, the knowledge base can't be used independently. In AI Studio, only the knowledge base added to the Knowledge Engine will be available while creating an AI Agent.</p>

        <h3 class="headingThree">How to add Knowledge Base to the Knowledge Engine?</h3>
        <ul class="steps-list">
            <li>Log in to anydone account.</li>
            <li>Click on the Automation Tools icon from the left side of your dashboard</li>
            <li>Under AI, select Knowledge Engine from the options.</li>
            <li>Click the + button from the bottom-right corner.</li>
            <li>Enter a name and description.</li>
            <li>Under Source, select Knowledge Base.</li>
            <li>Under Knowledge Base, select the knowledge base you’ve created.</li>
            <li>Select the Model.
            (here,you can choose between the default Anydone model or a model created with a third party)
            </li>
            <li>Finally, click the Create button.</li>
        </ul>
        `
    },
    "what-is-prompt":{
        title:"What is Prompt ?",
        content:`
        <p> A prompt is a set of instructions or guidance that you provide to an AI Agent to help it generate the response in a way you prefer. It’s like giving the AI Agent specific directions on how to approach a task. </p>

        <p>Think of it as a way to tell the AI Agent exactly what you want it to do, how to do it, and what to focus on. The more specific and detailed the prompt, the better the AI can tailor its response to match your needs.</p>

        <p><span class="underline-text">For example:</span> <br>
        If you want the AI to generate a piece of text, you could give it a prompt that tells it the tone (formal, casual), the style (short sentences, detailed explanations), and the topic (focused on a specific concept). In essence, a prompt acts as a “blueprint” that guides the AI in producing content that aligns with your expectations.</p>

        <p>In the anydone, prompts can be integrated into various AI models to help users get the most precise and tailored output for their specific needs.</p>

        <p>Here’s how prompts can work with each AI model:</p>

        <ul class="no-listing">
            <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Knowledge Engine</span> <br>
                A prompt for the Knowledge Engine helps guide how the AI pulls and presents information from your knowledge base, Intent, and Entity. It could include instructions on which type of data to prioritize (e.g., recent information, most relevant facts), or how to format the response (e.g., in bullet points or a paragraph).

                <p class="text-blue">Learn how to create knowledge engine</p>
                <p><strong>Example:</strong> If you want the AI Agent to extract information or data in a specific format or in a concise manner, create a prompt and add it.</p>
            </li>

            <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Information Retrieval</span><br>
                In an Information Retrieval model, a prompt defines the search parameters for what the AI Agent should look for. You could direct the AI to retrieve information from specific sources, prioritize certain keywords, or ensure the result is concise and to the point.

                <p class="text-blue">Learn how to create information retrieval</p>
                <p><strong>Example:</strong> If you want the AI Agent to extract information or data in a specific format or in a concise manner, create a prompt and add it.</p>
            </li>

             <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Code Generation</span><br>
                For Code Generation, prompts allow the user to specify what kind of code they need, the language they prefer, and the overall structure of the solution. This helps the AI Agent generate more precise, usable code based on the user’s requirements.

                <p class="text-blue">Learn how to create code generation </p>
                <p><strong>Example:</strong> If you need a Python script that connects to a database and retrieves user data, you could prompt the AI with specific details like “Generate a Python script using MySQL and pandas to query and display user data.”</p>
            </li>

             <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Code Review</span><br>
                When using a Code Review AI model, a prompt can guide the AI to focus on certain aspects of the code, such as syntax, performance, or security. You can also specify whether you want it to check for coding standards or suggest improvements.

                <p class="text-blue">Learn how to create code review</p>
                <p><strong>Example:</strong> If you submit your Python code for review, you might prompt the AI to “Check for efficiency issues and suggest improvements for readability” or “Identify potential security risks in the code.”</p>
            </li>

             <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Grammar Check</span><br>
                A prompt for Grammar Check tells the AI what type of corrections you want, such as focusing on grammar, punctuation, or style. It can also instruct the AI to maintain the original tone of the text.

                <p class="text-blue">Learn how to create grammar check</p>
                <p><strong>Example:</strong>You could prompt the AI by saying, “Check for any grammar mistakes and suggest improvements for clarity while keeping the tone formal.” This would ensure the AI focuses on both grammar and style adjustments.</p>
            </li>

             <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Text Summarize</span><br>
                In the case of Text Summarization, a prompt can guide the AI on how brief or detailed the summary should be and whether to focus on certain key points or aspects of the text.

                <p class="text-blue">Learn how to create text summarize </p>
                <p><strong>Example:</strong> If you have a long research paper, you might prompt the AI by saying, “Summarize this article in 200 words, focusing on the main arguments and conclusions.” This will ensure the summary is both concise and relevant to what you care about.</p>
            </li>

             <li>
                <span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Text Generation</span><br>
                For Text Generation, a prompt directs the AI on how to craft a response or create new content. You can specify the tone, style, length, and subject matter. This is useful for generating articles, blog posts, or other written materials.

                <p class="text-blue">Learn how to create text generation</p>
                <p><strong>Example:</strong> If you want the AI to write a blog post on "sustainable fashion," you could prompt it with something like, “Write a 500-word blog post on sustainable fashion trends, in a conversational tone, highlighting key eco-friendly brands.”</p>
            </li>
        </ul>

        <h3 class="headingThree">How to Create a Prompt ?</h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click on the Automation Tool-like icon.</li>
            <li>Under the AI section, select Prompts from the options.</li>
            <li>Enter a name and description for your prompt.</li>
            <li>Under the "Prompt" field, enter your desired prompt.</li>
            <li>Click on the Create button.</li>
        </ul>
        `
    },
    "knowledge-engine":{
        title:"What is a Knowledge Engine ? | anydone",
        content:`

        <p>The Knowledge Engine of anydone is the central hub where all AI tools come together to build AI models for automated responses. Within the Knowledge Engine, users can define, test, and optimize AI models that will drive contextual and accurate responses in their automation workflows.</p>

        <h3>How the Knowledge Engine Works:</h3>
        <ul class="no-listing">
            <li> - <strong>Define the Source: </strong>
                Users begin by selecting the source of knowledge that will power their AI model. This could include:
                    <ul class="no-listing">
                        <li> - <strong> Knowledge Base, Knowledge Graph, Intent Collection, or Generative Model.</strong>
                        </li>
                    </ul>
            </li>
            <li> - <strong> Select the Specific Source: </strong>
                 Once the source type is selected, anydone users need to select the exact resource they’ve already created. For Example: if the defined source is Knowledge base then you need to select Knowledge Base that you have created earlier.
            </li>
            <li> - <strong> Choose the Model:  </strong>
                    Users can then need to choose the model; 
                        <ul class="no-listing">
                            <li> - <strong> Default anydone Model:</strong>
                            An inbuilt option from anydone designed for quick and efficient use.
                            </li>
                            <li> - <strong> Third-Party Models:</strong>
                            External AI models configured for more specialized or advanced needs.
                            </li>
                        </ul>
            </li>
             <li> - <strong> Test and Refine the Model: </strong>
                 The Knowledge Engine allows users to test their models, ensuring accuracy and relevance in the responses generated.
            </li>
            <li> - <strong> Deploy the Model: </strong>
                Once tested and validated, the AI model is ready to be used in building AI Agents. These agents can then handle queries, automate tasks, and deliver intelligent interactions based on the resources and models configured in the Knowledge Engine.
            </li>
        </ul>

        <h3>How to create a Knowledge Engine?</h3>
        <p>To create a Knowledge Engine, make sure you have already created following AI tools:</p>

        <ul>
            <li>Knowledge Base</li>
            <li>Intent Collection</li>
            <li>Third-Party Models</li>
        </ul>

        <p>These AI tools serve as the foundation for building a functional Knowledge Engine. And from AI Studio you can use these created AI tools to build an AI Agent that works tailored to your business needs autonomously.</p>

        <h3>Follow the steps below to create Knowledge Engine</h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click on the Automation Tools icon from the left side of your dashboard.</li>
            <li>Under AI, select Knowledge Engine from the options.</li>
            <li>Click on the Source drop-down and select a Knowledge Engine source.
            (Here, we’ve selected Knowledge Base as the source.)
            </li>
            <li>Choose the specific tool created under the selected source.
            (For example, if you’ve selected Knowledge Base as the source, select the specific Knowledge Base you want to add.)
            </li>
            <li>Finally, click the Create button.</li>
        </ul>

        <h3>Adding third party model (Text Generation) to Knowledge Engine</h3>
        <p>In anydone, you can seamlessly integrate third-party models from providers like OpenAI, Amazon AWS, Google, and more. If you create a third-party model for Text Generation, it must be added to the Knowledge Engine. </p>

        <p>However, for models other than Text Generation—such as Speech to Text, Text to Speech, Image Understanding and more —you will need to create a Process, to use these models in AI Studio. </p>

        <h3>Follow the below steps to add third party model(Text Generation) to Knowledge Engine:</h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click on the Automation Tool icon on the left-hand side of your dashboard.</li>
            <li>Under AI, select Knowledge Engine from the options.</li>
            <li>Click the + button in the bottom-right corner.</li>
            <li>Enter a name and description for the Knowledge Engine.</li>
            <li>Under Source, select Generative Model.</li>
            <li>Under Model, choose the third party model you created for Text Generation.</li>
            <li>Finally, click the Create button.</li>
        </ul>

        <h3>How to Test Knowledge Engine? </h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click on the Automation Tool icon on the left-hand side of your dashboard.</li>
            <li>Under AI, select Knowledge Engine from the options.</li>
            <li>Here, you will see a list of all the Knowledge Engines you’ve created.</li>
            <li>Navigate to the Knowledge Engine you want to test</li>
            <li>Click on the three dots next to it and select the Try Out option.</li>
            <li>In the Query field, enter a prompt relevant to the Knowledge Engine you’ve created.</li>
            <li>Click the Submit button.</li>
            <li>Now, view the output generated for your query.</li>
        </ul>

        <h3>How to integrate a Third Party Model ? </h3>
        <p>Anydone enables effortless integration with third-party platforms such as OpenAI, Google, Amazon AWS, and more.</p>

        <p>To begin integrating a third-party model, you’ll need to create Secrets first, which serve as the API or authorization keys required to connect anydone with your chosen platform and its models.</p>

        <p>Once a Third-Party Model is created, the next step depends on its type. For example, if the model is Text Generation, it must be added to the Knowledge Engine.</p>

        <p>Whereas, for models other than Text Generation, such as Speech to Text, Text to Speech, Image Understanding and more, you’ll need to create a Process then only you will be able to use these third party models in AI Studio to create AI Agents.</p>

        <h3>Integrating OpenAI’s Third Party Model</h3>
        <ul class="steps-list">
            <li>Log in to your anydone account.</li>
            <li>Click on the Automation Tool icon from the left side of the dashboard.</li>
            <li>Under AI, select the Third Party Model.</li>
            <li>Click the + button from the bottom-right corner.</li>
            <li>Enter a name.</li>
            <li>Under Provider, select the OpenAI.</li>
            <li>Under Type, choose your preferred model.
            (Model Type: Text Generation, Speech to Text, Text to Speech, Image Understanding)
            </li>
            <li>Under API Key, select the Secrets you created for the model.</li>
            <li>Configure the Model Settings according to your model type and your needs.</li>
            <li>Finally, click the Create button.</li>
        </ul>
        <p class="text-blue"><i class="fa-solid fa-arrow-right-long"></i> Learn how to create secrets</p>


        <h3>Integrating Amazon AWS’s Third Party Model </h3>
        <ul class="steps-list">
            <li>Log in to your Anydone account.</li>
            <li>Click on the Automation Tool icon on the left side of the dashboard.</li>
            <li>Under AI, select the Third Party Model.</li>
            <li>Click the + button from the bottom-right corner.</li>
            <li>Enter a name.</li>
            <li>Under Provider, select Amazon AWS.</li>
            <li>Under Type, choose your preferred model type (Text Generation, Speech to Text, Text to Speech, or Image Understanding).</li>
            <li>Under Authorization Token, select the Access Key and Secret Key corresponding to the model.</li>
            <li>Under Location, select the relevant region.</li>
            <li>Configure the Model Settings based on the model type and your specific needs.</li>
            <li>Click the Create button.</li>
        </ul>
        
        <strong>Tips: </strong>
        <ul class="no-listing">
            <li>- Make sure that you select the region where your AWS service is hosted. </li>
            <li>- While integrating Amazon AWS, you need to create two separate secrets: one for the Access Key and another for its Secret Key.</li>
        </ul>
        <p class="text-blue"><i class="fa-solid fa-arrow-right-long"></i> Learn how to create secrets.</p>

        <h3>Integrating Third Party Model Google</h3>
        <ul class="steps-list">
            <li>Log in to your Anydone account.</li>
            <li>Click on the Automation Tools icon from the left side of your dashboard.</li>
            <li>Under AI, select the Third Party Model.</li>
            <li>Click the + button from the bottom-right corner.</li>
            <li>Enter a name.</li>
            <li>Under Provider, select Google.</li>
            <li>Under Authorization Token > Credential, choose the Secret corresponding to the model.</li>
            <li>Under Location, select the relevant region.</li>
            <li>Under Project, enter the relevant project ID.</li>
            <li>Configure the Model Settings based on the model type and your specific requirements.</li>
            <li>Click the Create button.</li>
        </ul>
        <p class="text-blue"><i class="fa-solid fa-arrow-right-long"></i> Learn how to create secrets.</p>

        <strong>Tips: </strong>
        <ul class="no-listing">
            <li>- Make sure that you select the region where your Google Cloud service is hosted. </li>
            <li>- Enter the exact project ID you have used while creating your project in Google Cloud.</li>
            <li>- How to add third party model - Text Generation in Knowledge Engine</li>
            <li>- How to create Process with third party models - Text to Speech, Speech to Text and Image Understanding.</li>
        </ul>
        `
    },
    "flowcess":{
        title:"How to create Flowcess?",
        content:`
        <ul class="steps-list">
            <li>Log in to your Anydone account.</li>
            <li>Click on the Projects icon from the left side of your dashboard.</li>
            <li>Note: If you haven’t created a project yet, create one and continue.</li>
            <li>Click the + button next to your project name.</li>
            <li>From the Project Folders options, select Flowcess.</li>
            <li>Enter a name for your Flowcess folder.</li>
            <li>Click the Create button.</li>
        </ul>

        <strong>Tips</strong>
        <ul class="no-listing">
            <li>- One needs to be Project Owner, or need to have permission to create a project folder.</li>
        </ul>
        `
    },
    "secrets":{
        title:"Secrets",
        content:`   
        <p>Secrets is where you can add the API keys of your preferred third-party platforms. This allows seamless integration of third-party models into anydone. Here, users need to have the API keys of their preferred platform and its models. 
        </p>

        <p>Once the keys are added and Secrets are created, you can use them in Third Party Integration so that you can enhance the capabilities of their AI Agents.</p>

        <ul class="steps-list">
            <li>Log in to your Anydone account.</li>
            <li>Click on the Automation Tools icon on the left side of your dashboard.</li>
            <li>Navigate to Secrets from the options.</li>
            <li>Click the + button from the bottom-right corner.</li>
            <li>Enter a name and description.</li>
            <li>Under Value, add the OpenAI API key.</li>
            <li>Define the Scope for the key.</li>
            <li>Click the Create button.</li>
        </ul>

        <strong>Tips:</strong>
        <ul>
        <li>Once created, the API key that you’ve added under Value will not be visible to anyone, including yourself.</li>
        <li>While creating Secrets for Amazon AWS, you need to create two secrets separately, one for Access Keys and another for Secret Keys.</li>
        <li>After creating Secrets, you’ll need to add them while integrating their model. For secrets, terminologies may vary—for OpenAI it’s API Keys, for Google it’s Credentials, and for Amazon AWS it’s Access and Secret Keys.</li>
        <li>In the Scope section, define the application of the model. If it’s for a specific project, select the project; otherwise, choose a workspace.</li>
        </ul>
        `
    },
    "api":{
        title:"API",
        content:`
        <p>How to Create an API Collection in Anydone
        To connect external APIs with your AI Agents, you can either import an existing API Collection or create one manually. Here’s how to get started:
        </p>

        <ul class="steps-list">
            <li>Go to your Anydone dashboard.</li>
            <li>Click the Automation Tool icon.</li>
            <li>Navigate and select API Option.</li>
            <li>From the options, choose API to manage or create your API collections.</li>
            <li>Click the + button from the right corner.</li>
            <li>Here, you’ll see two options:</li>
            <ul>
                <li><span class="underline-text">Import:</span> Upload an existing API collection in supported formats.</li>
                <li><span class="underline-text">Create API Collection:</span> Build a new API collection manually.</li>
            </ul>
        </ul>

        <h3>Create API Collection (Manual Setup):</h3>
        <ul class="steps-list">
            <li>Click on Create API Collection.</li>
            <li>Enter a name and optional description for your collection.</li>
            <li>Click Create.</li>
        </ul>

        <h3>Add an API Endpoint:</h3>
        <ul class="steps-list">
            <li>Click on the newly created API Collection.</li>
            <li>Then click the +  button.</li>
            <li>Fill in all required details like method, URL, headers, query parameters, and body 
            (For example, you can start with a simple GET request to test.)</li>
            <li>Once all the details are entered, click Create.</li>
        </ul>

        <h3>API Collection Creation</h3>
        <p>In Anydone, API Collections let you connect your AI Agents with external services or databases using API requests. Here’s how each step works:</p>

        <ul class="no-listing">
            <li><span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> URL & Method </span> <br>
            Start by selecting the API method based on your use case — options include GET, POST, PUT, PATCH, or DELETE. Once selected, enter the appropriate URL endpoint that the method will call.
            </li>

            <li><span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Headers </span> <br>
            Headers in an API request carry additional information between the client and server. They're used for things like authorization, content type, or custom configurations.
                <ul class="no-listing">
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Key:</strong> Represents the name of the header (e.g., Authorization, Content-Type).
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Type:</strong> Choose between Static (fixed value) or Dynamic (value pulled from variables or inputs).
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Value:</strong> The actual value corresponding to the key (e.g., a token or MIME type).
                    </li>
                </ul>
            You can add multiple headers depending on your API’s requirements.
            </li>

            <li><span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Query Parameters</span> <br>
            Query Parameters are used to send additional data to the server via the URL — typically in key-value pairs like ?search=term.
                 <ul class="no-listing">
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Key:</strong> The parameter name (e.g., userId, limit, sort).
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Type:</strong> Choose Static for fixed values or Dynamic for values that may change during runtime.
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> Value:</strong> The value associated with the key (e.g., 123, asc, 10).
                    </li>
                </ul>
            These are useful for filtering, searching, or refining data sent through a GET request.
            </li>

            <li><span class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Body</span> <br>
            The Body of an API request carries the main data payload — especially in POST, PUT, or PATCH methods. <br>
            You can select from the following formats:
                 <ul class="no-listing">
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> none:</strong> No body is sent with the request.
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> form-data:</strong> Sends data as key-value pairs; commonly used for file uploads.
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> form-url encoded:</strong> Similar to form-data but URL-encoded; often used in HTML form submissions.
                    </li>
                    <li> <strong><i class="fa-solid fa-arrow-right-long"></i> raw:</strong> Allows you to send raw JSON, XML, or plain text, giving you full control over the payload structure.
                    </li>
                </ul>
            </li>
        </ul>
        <p>Once the API is created, you need to create a Process so you can use it to build an AI Agent.</p>

        <ul class="steps-list">
        <li>Log in to your Anydone dashboard.</li>
        <li>Click on Process.</li>
        <li>Click the + button from the right side corner.</li>
        <li>Enter a name and description for your Process.</li>
        <li>Click the dropdown under Type.</li>
        <li>Search and select API.</li>
        <li>Under API Collection, select the collection you created earlier.</li>
        <li>Then, under API Endpoints, choose the API endpoint you previously added.</li>
        <li>Add the required details.</li>
        <li>Finally, click the Create button.</li>
        </ul>
        `
    },
    "function":{
        title:"What is a function in anydone?",
        content:`

        <p>In anydone, the WebAssembly is referred to as a function. Here, you can upload or use the built- in WebAssembly functions.  To use the built-in functions, you can directly access it from our AI Studio.</p>

        <h3>How to create a Function?</h3>
        <p>Below, we explain each built in models in detail:</p>

        <h3 class="underline-text">For Cryptographic Operations</h3>

        <ul class="no-listing">
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Verify-sha256-pss:</strong> Verifies if a SHA-256 PSS signature matches the original data and public key.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Verify-sha256-pkcs-v1:</strong> Checks if a SHA-256 PKCS#1 v1.5 signature is valid for the given data and public key.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Generate-sha256-pss-signature:</strong> Creates a SHA-256 PSS signature from the input data using a private key.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Generate-sha256-pkcs-v1-signature:</strong> Generates a SHA-256 PKCS#1 v1.5 signature from the input data using a private key.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Base64-decode:</strong> Converts a Base64-encoded string back into its original form.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Base64-encode:</strong> Converts raw data into a Base64-encoded string.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Sha512-compare:</strong> Compares two SHA-512 hash values to determine if they are identical.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Sha256-compare:</strong> Similar to "Sha512-compare," but uses the SHA-256 hashing algorithm.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Sha512:</strong> Generates a SHA-512 hash from input data.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Sha256:</strong> Generates a SHA-256 hash from input data.</li>
        </ul>

        <h3 class="underline-text">Date and Time Operations</h3>
        <ul class="no-listing">
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Subtract date:</strong> Calculates the difference between two dates.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Parse datetime:</strong> Converts a string representation of a date and time.</li>
        </ul>

        <h3 class="underline-text">For Validation and Randomization</h3>
        <ul class="no-listing">
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Is url valid:</strong> Checks whether a given string is a valid URL.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Random number:</strong> Generates a random number within a specified range.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Random string:</strong> Generates a random alphanumeric string of a specified length.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Uuid v4:</strong> Generates a Universally Unique Identifier (UUID) using version 4.</li>
        </ul>

        <h3 class="underline-text">For Text Manipulation Operations</h3>
        <ul class="no-listing">
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Text remove:</strong> Removes specific substrings from a given text.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Get JSON object:</strong> Parses and converts strings into a structured JSON object.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Text split:</strong> Splits a string into an array of substrings based on a delimiter.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Text replace:</strong> Replaces occurrences of a substring within a text with another substring.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Count word:</strong> Counts the number of words in a given text.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Lowercase text:</strong> Converts all characters in a string to lowercase.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Uppercase text:</strong> Converts all characters in a string to uppercase.</li>
        </ul>

        <h3 class="underline-text">For Mathematical Operations</h3>
        <ul class="no-listing">
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Division math:</strong> Performs division between two numbers.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Product math:</strong> Multiplies two or more numbers together.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Diff math:</strong> Calculates the difference (subtraction) between two numbers.</li>
        <li><strong class="underline-text"><i class="fa-solid fa-arrow-right-long"></i> Sum math:</strong> Adds two or more numbers together.</li>
        </ul>
        `
    }
}